<template>
  <div
    class="my-8 relative bg-white w-full rounded-xl border border-solid border-brand-600 p-3 sm:p-4 focus-within:shadow-[0_0_0_1px_rgba(38,89,87,0.5)] transition-shadow duration-200 ease-in-out"
  >
    <div class="flex items-center gap-x-2">
      <SearchGreenIcon />
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="searchPlaceholder"
        class="text-sm sm:text-base w-full bg-white text-gray-500 placeholder-gray-500 focus:outline-none"
        @focus="showDropdown = true"
        @blur="hideDropdown"
      />
    </div>

    <transition name="fade">
      <ul
        v-if="showDropdown && filteredBenchmarks.length > 0"
        class="absolute -ml-4 z-10 w-full bg-white rounded-lg mt-3 shadow-lg max-h-[400px] overflow-y-scroll scrollbar scrollbar-search"
      >
        <li
          v-for="benchmark in filteredBenchmarks"
          :key="benchmark.country_code"
          @mousedown.prevent="goToCountry(benchmark.country_code)"
          class="px-4 py-2 hover:bg-gray-200 cursor-pointer text-gray-700 text-sm sm:text-base"
        >
          {{ benchmark.name }}
        </li>
      </ul>
    </transition>

    <transition name="fade">
      <div
        v-if="showDropdown && searchQuery && filteredBenchmarks.length === 0"
        class="absolute -ml-4 z-10 w-full bg-white rounded-lg mt-3 shadow-lg text-gray-500 italic px-4 py-2 text-sm sm:text-base"
      >
        <Translation
          name="benchmarks_home.search_no_results"
          default="No Results Found"
        />
      </div>
    </transition>
  </div>
  <div class="bg-white rounded-lg p-8 flex flex-col">
    <h3 class="text-gray-600 font-bold text-sm sm:text-base">
      <Translation
        name="benchmarks_home.about_title"
        default="About the IDH Benchmark Finder"
      />
    </h3>
    <p class="my-2 text-gray-600 pb-4 text-sm sm:text-base">
      <Translation
        name="benchmarks_home.about_text"
        default="This tool helps companies find credible living wage benchmarks from every country they source from. By searching a specific region, you will have access to a list of the benchmarks available. From this list, you can directly go to the website of the chosen benchmark methodology."
      />
    </p>
    <p class="my-2 text-gray-600 pb-4 text-sm sm:text-base">
      <Translation
        name="benchmarks_home.about_contact"
        default="Please contact IDH if you have any questions at"
      />
      <span class="underline">
        <a href="mailto:livingwagematrix@idhtrade.org">
          livingwagematrix@idhtrade.org</a
        ></span
      >
      .
    </p>
    <p class="pt-4 text-gray-400 text-sm border-t border-solid border-gray-300">
      <span class="font-bold">
        <Translation name="benchmarks_home.disclaimer" default="Disclaimer:" />
      </span>
      <Translation
        name="benchmarks_home.disclaimer_text"
        default="Please note that IDH is not responsible and cannot be held accountable for the benchmark values. This tool has for sole purpose to display the values in a uniform way. If you have any questions or comments regarding the benchmark values, please contact the methodology directly."
      />
    </p>
  </div>
  <div
    class="rounded-lg p-6 mt-2 w-full text-left bg-no-repeat bg-cover"
    :style="{ backgroundImage: `url(${bannerImage})` }"
  >
    <h2 class="text-brand-600 text-lg sm:text-xl md:text-2xl font-bold">
      <Translation
        name="benchmarks_home.banner_title"
        default="Salary Matrix"
      />
    </h2>
    <p class="text-sm sm:text-base text-brand-600 mb-4">
      <Translation
        name="benchmarks_home.banner_subtitle"
        default="Measure the living wage gap in your supply chain"
      />
    </p>
    <a
      :href="rootPath"
      class="text-brand-700 bg-white py-2 px-3 rounded-lg text-sm font-semibold flex items-center gap-2 w-fit"
    >
      <Translation
        name="benchmarks_home.banner_button_text"
        default="Open Salary Matrix"
      />
      <ExternalLinkIcon class="w-5 h-5" />
    </a>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import SearchGreenIcon from "../icons/SearchGreenIcon.vue";
import ExternalLinkIcon from "../icons/ExternalLinkIcon.vue";
import { useTranslation } from "../aft/useTranslations";

const searchQuery = ref("");
const showDropdown = ref(false);

const locale = window.location.pathname.split("/")[1] || "en";

const props = defineProps({
  benchmarks: {
    type: Array,
    required: true,
  },
  rootPath: {
    type: String,
    required: true,
  },
  bannerImage: {
    type: String,
    required: true,
  },
});

const { _t: translate } = useTranslation();

const searchPlaceholder = translate(
  "benchmarks_home.search_placeholder",
  "Search a benchmark",
);

const filteredBenchmarks = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];
  return props.benchmarks.filter((b) => b.name.toLowerCase().startsWith(q));
});

function hideDropdown() {
  setTimeout(() => (showDropdown.value = false), 100);
}

function goToCountry(countryCode) {
  window.location.href = `/${locale}/benchmark-finder/${countryCode}`;
}
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
